export default {
  id: "authorizations",
  title: "Authorizations",
  section: "Daily Operations",
  order: 30,
  keywords: "authorization auth units hours bulk import caseload budget report migrate future low hours email PDF counselor",
  body: `
## What it is

Authorizations are the funding behind every billable shift: VR
authorizations track **hours**, DSPD and Private Pay track **units**
(usually quarter-hours). Approving a shift deducts from the matching
authorization for that client + service code. Manage them on the
Authorizations page or from a client's Authorizations tab.

## Step by step: creating one by hand

1. Authorizations → **Add New Authorization**.
2. Pick the client (or tick **New** to type a new client's name).
3. Fill the payer-specific identity fields:
   - **VR**: counselor (searchable — this drives invoice routing), client
     VR ID, and the authorization number from the paperwork.
   - **DSPD/PP**: the DSPD PID / PP client ID; the auth number is
     generated automatically.
4. Fiscal year, **begin and end dates**, then the hours/rate (VR) or units
   (DSPD) budget fields for the service code(s).
5. VR non-JD auths have a **Low Hours Threshold** (default 10) — crossing
   it triggers the authorization-request email flow (below).
6. **Upload PDF** attaches the authorization paperwork; it displays right
   in the dialog and rides along on invoices.

## Step by step: bulk import from the DSPD caseload file

Instead of typing DSPD auths one by one, upload the state's **Provider
Caseload Budget Report**:

1. Open the authorization dialog → **Bulk Import from File**.
2. Upload the **CSV** version (not XLS — Excel drops leading zeros from
   PIDs and breaks matching).
3. Optionally set the **Override Begin/End Date** fields — use these when
   importing plan-year authorizations, since the caseload file carries
   monthly windows. Leave blank to use the file's dates.
4. Import, then read the results: **Created / Updated / Skipped / Errors**
   tiles with per-row detail.

How rows are matched and applied:

- Clients match by **DSPD PID**, then by name; unmatched rows **create**
  the client.
- **A matched authorization is always updated — the state's dollar and
  unit changes are never skipped.** If the begin dates line up (or the old
  auth had expired) the dates roll forward too; if an active auth has a
  different start date, only the financials refresh and the existing date
  window is kept, so shift coverage never shrinks.

## Running low or running out

- The **nightly check** looks at today plus the next two days and flags
  any shift that would exceed the client's remaining balance — the shift
  turns **Error** until the authorization is extended or the schedule
  changes. After fixing, the shift's **Check Auth** button re-runs the
  check immediately.
- Creating or approving a shift over the limit shows an acknowledgment
  warning rather than silently blocking — you decide.
- When a VR authorization crosses its low-hours threshold, the system
  drafts an **authorization request email** to the counselor (using the
  per-code AI prompt from Settings). Drafts land on the **Dashboard's
  Email Authorization Tracking** queue for your approve / reject /
  regenerate before anything sends.

## Future authorizations & migration

Work sometimes continues while new paperwork is pending. Shifts approved
without an active authorization accrue against a placeholder **Future**
authorization. When the real authorization arrives:

1. Create it (or import it).
2. On the Future authorization, use **Migrate** — the accrued hours/units
   transfer onto the real authorization and the placeholder clears.
`,
};
